import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TextField,
    Button,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import { DateTime } from 'luxon';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import '../styles/pdfExport.css';

const API_URL = process.env.REACT_APP_API_URL;

const ProgramaTelas = () => {
    const [asignaciones, setAsignaciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fechaInicio, setFechaInicio] = useState(DateTime.now().toFormat('yyyy-MM-dd'));
    const [fechaFin, setFechaFin] = useState(DateTime.now().toFormat('yyyy-MM-dd'));

    const fetchAsignaciones = async () => {
        if (!fechaInicio || !fechaFin) {
            setError('Por favor ingresa las fechas de inicio y fin');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${API_URL}/asignar-referencia?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`,
                { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );

            if (!response.ok) {
                throw new Error('Error al cargar asignaciones');
            }

            const data = await response.json();
            
            // Ordenar por máquina y fecha
            const sorted = data.sort((a, b) => {
                // Primero ordenar por número de máquina
                const numA = parseInt(a.machineNumber.replace(/[^0-9]/g, '')) || 0;
                const numB = parseInt(b.machineNumber.replace(/[^0-9]/g, '')) || 0;
                if (numA !== numB) return numA - numB;
                
                // Luego por fecha de inicio
                return new Date(a.fechaInicio) - new Date(b.fechaInicio);
            });

            setAsignaciones(sorted);
        } catch (err) {
            console.error('Error al cargar asignaciones:', err);
            setError('Error al cargar el programa de telas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAsignaciones();
    }, []);

    const formatFecha = (fecha) => {
        return DateTime.fromISO(fecha).toFormat('dd/MM/yyyy');
    };

    // fechaFin se almacena con +1 día en backend (para el calendario),
    // así que la restamos para mostrar la fecha real al usuario
    const formatFechaFin = (fecha) => {
        return DateTime.fromISO(fecha).minus({ days: 1 }).toFormat('dd/MM/yyyy');
    };

    const calcularDias = (inicio, fin) => {
        // fechaFin ya tiene +1 día por el backend, el diff da los días correctos sin sumar más
        return Math.round(DateTime.fromISO(fin).diff(DateTime.fromISO(inicio), 'days').days);
    };

    const generarPDF = () => {
        const input = document.getElementById('tabla-programa-telas');
        if (!input) return;
        
        input.classList.add('pdf-export');
        
        html2canvas(input, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            windowHeight: input.scrollHeight
        }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'letter'  // Formato carta
            });
            
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const marginX = 10;
            const marginY = 10;
            
            // Área disponible para contenido
            const contentWidth = pdfWidth - (marginX * 2);
            const contentHeight = pdfHeight - (marginY * 2);
            
            // Calcular dimensiones de la imagen
            const imgWidth = contentWidth;
            const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
            
            let heightLeft = imgHeight;
            let position = marginY;
            
            // Primera página
            pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
            heightLeft -= contentHeight;
            
            // Páginas adicionales si es necesario
            while (heightLeft > 0) {
                position = -(imgHeight - heightLeft) + marginY;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
                heightLeft -= contentHeight;
            }
            
            const nombreArchivo = `programa_telas_${DateTime.now().toFormat('yyyyMMdd_HHmm')}.pdf`;
            pdf.save(nombreArchivo);
            input.classList.remove('pdf-export');
        });
    };

    // Agrupar por máquina
    const agrupadoPorMaquina = asignaciones.reduce((acc, asig) => {
        const maquina = asig.machineNumber;
        if (!acc[maquina]) acc[maquina] = [];
        acc[maquina].push(asig);
        return acc;
    }, {});

    // Consolidar filas consecutivas de la misma referencia en un solo bloque
    const consolidarBloques = (asigs) => {
        if (!asigs.length) return [];
        const bloques = [];
        let actual = { ...asigs[0] };
        for (let i = 1; i < asigs.length; i++) {
            const curr = asigs[i];
            // fechaFin almacenada = día siguiente real, así que coincide con fechaInicio del día siguiente
            const prevFinDate = DateTime.fromISO(actual.fechaFin).toISODate();
            const currInicioDate = DateTime.fromISO(curr.fechaInicio).toISODate();
            if (actual.referencia === curr.referencia && prevFinDate === currInicioDate) {
                actual = { ...actual, fechaFin: curr.fechaFin }; // extender el bloque
            } else {
                bloques.push(actual);
                actual = { ...curr };
            }
        }
        bloques.push(actual);
        return bloques;
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 3, color: '#071525' }}>
                    Programa de Telas por Máquina
                </Typography>

                {/* Filtros */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        label="Fecha Inicio"
                        type="date"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 200 }}
                    />
                    <TextField
                        label="Fecha Fin"
                        type="date"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 200 }}
                    />
                    <Button 
                        variant="contained" 
                        onClick={fetchAsignaciones}
                        disabled={loading}
                        sx={{ height: 56 }}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Consultar'}
                    </Button>
                    <Button 
                        variant="contained"
                        color="error"
                        onClick={generarPDF}
                        disabled={loading || asignaciones.length === 0}
                        sx={{ height: 56 }}
                    >
                        Exportar PDF
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Resumen */}
                {asignaciones.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Total de asignaciones: {asignaciones.length} | 
                            Máquinas: {Object.keys(agrupadoPorMaquina).length}
                        </Typography>
                    </Box>
                )}

                {/* Tabla */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : asignaciones.length === 0 ? (
                    <Alert severity="info">
                        No se encontraron asignaciones de telas para el período seleccionado
                    </Alert>
                ) : (
                    <Box id="tabla-programa-telas" className="tabla-container">
                        {/* Encabezado para PDF */}
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#071525' }}>
                                Programa de Telas por Máquina
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Período: {formatFecha(fechaInicio)} - {formatFecha(fechaFin)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total asignaciones: {asignaciones.length} | Máquinas: {Object.keys(agrupadoPorMaquina).length}
                            </Typography>
                        </Box>
                        
                        <TableContainer component={Paper} sx={{ maxHeight: '600px', '&.pdf-export': { maxHeight: 'none' } }}>
                            <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#071525', color: 'white' }}>
                                        Máquina
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#071525', color: 'white' }}>
                                        Referencia (Tela)
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#071525', color: 'white' }}>
                                        Fecha Inicio
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#071525', color: 'white' }}>
                                        Fecha Fin
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#071525', color: 'white' }}>
                                        Días
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.keys(agrupadoPorMaquina).sort((a, b) => {
                                    const numA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
                                    const numB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
                                    return numA - numB;
                                }).map((maquina) => (
                                    <React.Fragment key={maquina}>
                                        {/* Fila de encabezado de máquina */}
                                        <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                            <TableCell 
                                                className="tabla-encabezado-maquina"
                                                colSpan={5} 
                                                sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#071525' }}
                                            >
                                                {maquina}
                                            </TableCell>
                                        </TableRow>
                                        {/* Filas consolidadas por bloque continuo de misma referencia */}
                                        {consolidarBloques(agrupadoPorMaquina[maquina]).map((asig, idx) => (
                                            <TableRow 
                                                key={asig._id || idx}
                                                sx={{ '&:hover': { backgroundColor: '#424242' } }}
                                            >
                                                <TableCell></TableCell>
                                                <TableCell sx={{ fontWeight: 500 }}>
                                                    {asig.referencia}
                                                </TableCell>
                                                <TableCell>
                                                    {formatFecha(asig.fechaInicio)}
                                                </TableCell>
                                                <TableCell>
                                                    {formatFechaFin(asig.fechaFin)}
                                                </TableCell>
                                                <TableCell>
                                                    {calcularDias(asig.fechaInicio, asig.fechaFin)} días
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default ProgramaTelas;
